const DEFAULT_BYPASS_ROUTES = ["certificate_lookup"];

// Self-service account routes are not sold as apps. Digital signature items
// live under profile.* and must not lock these account pages.
const ACCOUNT_ROUTE_ALLOWLIST = [
	"profile.index",
	"profile.edit",
	"profile.password",
];

const hasValues = (value) => Array.isArray(value) && value.length > 0;

const resolveCurrentRoute = (router, item) => {
	if (!item || !router) {
		return null;
	}

	if (item.routeName) {
		try {
			return router.resolve({ name: item.routeName }).route;
		} catch (error) {
			return null;
		}
	}

	if (!hasValues(item.dropdown)) {
		return null;
	}

	let candidateRoute = null;
	for (const dropdown of item.dropdown) {
		if (!dropdown?.routeName) {
			continue;
		}
		try {
			const route = router.resolve({ name: dropdown.routeName }).route;
			if (hasValues(route?.meta?.permissions)) {
				return route;
			}
			if (!candidateRoute) {
				candidateRoute = route;
			}
		} catch (error) {
			continue;
		}
	}

	return candidateRoute;
};

export const hasAppPermission = ({
	item,
	router,
	permissions = [],
	clientEnv = process.env.CLIENT_ENV,
}) => {
	if (!item) {
		return false;
	}
	if (item.externalLink) {
		return true;
	}
	if (hasValues(item.denied) && item.denied.includes(clientEnv)) {
		return false;
	}

	const currentRoute = resolveCurrentRoute(router, item);
	if (!currentRoute || !hasValues(currentRoute.meta?.permissions)) {
		return false;
	}

	return permissions.some((permission) =>
		currentRoute.meta.permissions.includes(permission)
	);
};

export const getAppAccessState = ({
	item,
	router,
	permissions = [],
	bypassRoutes = DEFAULT_BYPASS_ROUTES,
	clientEnv = process.env.CLIENT_ENV,
}) => {
	if (!item) {
		return { disabled: true, reason: "invalid" };
	}
	if (item.incomming) {
		return { disabled: true, reason: "incoming" };
	}
	if (!item.have_bought) {
		return { disabled: true, reason: "not_subscribed" };
	}
	if (item.externalLink || bypassRoutes.includes(item.routeName)) {
		return { disabled: false, reason: null };
	}

	const hasPermission = hasAppPermission({
		item,
		router,
		permissions,
		clientEnv,
	});
	if (!hasPermission) {
		return { disabled: true, reason: "no_permission" };
	}

	return { disabled: false, reason: null };
};

export const isAppDisabled = (context) => getAppAccessState(context).disabled;

const getRouteNamespace = (routeName) => {
	if (!routeName || typeof routeName !== "string") {
		return null;
	}
	return routeName.split(".")[0];
};

const nodeOwnsExactRoute = (node, routeName) => {
	if (!node || !routeName || node.incomming) {
		return false;
	}
	if (node.routeName === routeName) {
		return true;
	}
	return hasValues(node.dropdown) && node.dropdown.some((child) => nodeOwnsExactRoute(child, routeName));
};

const nodeOwnsRouteNamespace = (node, routeName) => {
	if (!node || !routeName || node.incomming) {
		return false;
	}
	if (node.routeName) {
		const nodeNamespace = getRouteNamespace(node.routeName);
		const routeNamespace = getRouteNamespace(routeName);
		if (nodeNamespace && nodeNamespace === routeNamespace) {
			return true;
		}
	}
	return hasValues(node.dropdown) && node.dropdown.some((child) => nodeOwnsRouteNamespace(child, routeName));
};

export const isRouteSubscribed = ({ routeName, groups = [] }) => {
	if (!routeName || !Array.isArray(groups)) {
		return true;
	}
	if (ACCOUNT_ROUTE_ALLOWLIST.includes(routeName)) {
		return true;
	}

	const apps = groups.flatMap((group) => Array.isArray(group?.apps) ? group.apps : []);
	const exactOwners = apps.filter((app) => nodeOwnsExactRoute(app, routeName));
	if (exactOwners.length) {
		return exactOwners.some((app) => app.have_bought !== false);
	}

	const namespaceOwners = apps.filter((app) => nodeOwnsRouteNamespace(app, routeName));
	if (!namespaceOwners.length) {
		return true;
	}

	return namespaceOwners.some((app) => app.have_bought !== false);
};
